import React, { useCallback, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Info, AlertTriangle, CheckCircle, Calendar, ArrowRight, Megaphone, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import api, { asItems, formatApiError } from "@/lib/api";

const NOTIF_TABS = [
  { id: "all", label: "All Alerts" },
  { id: "visits", label: "Site Visits" },
  { id: "demands", label: "Plots on Demand" },
  { id: "system", label: "System Alerts" },
];

const formatDateTime = (dtStr) => {
  if (!dtStr) return "—";
  try {
    const d = new Date(dtStr);
    return d.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + " · " + d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dtStr;
  }
};

const isNotificationRead = (n) => {
  if (n.status === "read") return true;
  if (n.read === true) return true;
  return false;
};

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [loadError, setLoadError] = useState("");
  const [partialError, setPartialError] = useState("");
  const [actionError, setActionError] = useState("");
  const [markingAll, setMarkingAll] = useState(false);
  const role = String(user?.role || "buyer").toLowerCase().replace("office_user", "office");
  const visitDashboardPath = ["buyer", "owner"].includes(role)
    ? "/dashboard/appointments"
    : role === "agent"
      ? "/dashboard/agent/visits"
      : role === "office"
        ? "/dashboard/office/site-visits"
        : "/dashboard/admin/site-visits/workbench";
  const demandDashboardPath = (notification) => {
    if (["buyer", "owner"].includes(role)) return "/dashboard/plot-demands";
    const leadId = notification?.raw_demand?.lead_id;
    if (role === "agent") return `/dashboard/leads${leadId ? `?select=${encodeURIComponent(leadId)}` : ""}`;
    if (role === "office") return "/dashboard/office/crm-leads";
    const demandId = notification?.raw_demand?.id || notification?.raw_demand?.plot_demand_id;
    return `/dashboard/admin/plots-on-demand/workbench${demandId ? `?select=${encodeURIComponent(demandId)}` : ""}`;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setPartialError("");
    setActionError("");
    const partialFailures = [];
    try {
      // 1. Fetch real DB notifications with high limit to show all
      const notifRes = await api.get("/notifications?limit=100");
      const notifItems = asItems(notifRes.data);

      // 2. Fetch site visits based on user role scope
      let visitItems = [];
      const scope = ["buyer", "owner"].includes(role) ? "mine" : (role === "agent" ? "agent" : (role === "office" ? "office" : "all"));
      try {
        const visitRes = await api.get(`/appointments?scope=${scope}`);
        const rawVisits = asItems(visitRes.data);
        
        // Format site visits as virtual notifications
        visitItems = rawVisits.map(v => {
          const statusUpper = String(v.status || "").toUpperCase();
          const isPast = ["VISITED", "COMPLETED", "CANCELLED"].includes(statusUpper);
          return {
            id: `visit-${v.site_visit_id || v.id}`,
            _id: `visit-${v.site_visit_id || v.id}`,
            type: "appointment",
            title: "Site Visit Update",
            message: ["buyer", "owner"].includes(role)
              ? `Your site visit for "${v.property?.title || "Property details unavailable"}" is scheduled for ${formatDateTime(v.scheduled_at || v.visit_at)}. Assigned agent: ${v.agent_name || "Not assigned"} (Status: ${v.status || "Unavailable"})`
              : `Upcoming site visit for "${v.property?.title || "Property details unavailable"}" at ${formatDateTime(v.scheduled_at || v.visit_at)}. Visitor: ${v.visitor_name || "Buyer details unavailable"} (${v.visitor_phone || "Phone unavailable"}) (Status: ${v.status || "Unavailable"})`,
            created_at: v.scheduled_at || v.created_at,
            read: isPast,
            status: isPast ? "read" : "unread",
            raw_visit: v,
          };
        });
      } catch (err) {
        partialFailures.push(`Site Visit updates: ${formatApiError(err) || "could not be loaded."}`);
      }

      // 3. Fetch plot demands based on user role
      // Buyer/owner and admin have existing row-scoped demand endpoints. Agent
      // and office portals rely on persisted assignment notifications here;
      // calling either /me or the admin directory for those roles would be the
      // wrong tenant scope and previously produced a hidden 403/empty result.
      let demandItems = [];
      const demandEndpoint = ["admin", "super_admin"].includes(role)
        ? "/admin/plot-demands"
        : ["buyer", "owner"].includes(role)
          ? "/v1/plot-demands/me"
          : "";
      if (demandEndpoint) {
        try {
          const demandsRes = await api.get(demandEndpoint);
          const rawDemands = asItems(demandsRes.data);
          const uid = user?.user_id || user?.id;
        
          const filteredDemands = rawDemands.filter(pd => {
            if (["buyer", "owner"].includes(role)) return String(pd.buyer_user_id) === String(uid);
            return true;
          });

          demandItems = filteredDemands.map(pd => {
            const requestId = String(pd.id || pd.plot_demand_id || "unavailable");
            const demandStatus = String(pd.status || "Unavailable").toUpperCase();
            const budget = pd.budget_max ?? pd.budget;
            const budgetText = budget != null && budget !== "" && Number.isFinite(Number(budget))
              ? `₹${Number(budget).toLocaleString("en-IN")}`
              : "Budget unavailable";
            return {
              id: `demand-${requestId}`,
              _id: `demand-${requestId}`,
              type: "demand",
              title: "Plots on Demand Status",
              message: ["buyer", "owner"].includes(role)
                ? (pd.agent_name
                    ? `Your Plots on Demand request (Ref: ${requestId.slice(0, 8)}) is assigned to agent ${pd.agent_name}.`
                    : `Your Plots on Demand request (Ref: ${requestId.slice(0, 8)}) is currently ${demandStatus}.`)
                : `Plots on Demand request from ${pd.buyer_name || "Buyer details unavailable"} (${budgetText}).`,
              created_at: pd.created_at,
              read: ["COMPLETED", "CANCELLED"].includes(demandStatus),
              status: ["COMPLETED", "CANCELLED"].includes(demandStatus) ? "read" : "unread",
              raw_demand: pd,
            };
          });
        } catch (err) {
          partialFailures.push(`Plots on Demand updates: ${formatApiError(err) || "could not be loaded."}`);
        }
      }

      // Merge all items and sort by created_at descending
      const merged = [...visitItems, ...demandItems, ...notifItems];
      merged.sort((a, b) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setNotifications(merged);
      setPartialError(partialFailures.join(" "));
    } catch (e) {
      setNotifications([]);
      setLoadError(formatApiError(e) || "Notifications could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [role, user?.id, user?.user_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const markRead = async (id) => {
    setActionError("");
    if (String(id).startsWith("visit-") || String(id).startsWith("demand-")) {
      setNotifications((prev) =>
        prev.map((n) => ((n.id === id || n._id === id) ? { ...n, read: true, status: "read" } : n))
      );
      return;
    }
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => ((n.id === id || n._id === id || n.notification_id === id) ? { ...n, read: true, status: "read" } : n))
      );
    } catch (requestError) {
      setActionError(formatApiError(requestError) || "The notification could not be marked as read.");
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    setActionError("");
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, status: "read" })));
    } catch (requestError) {
      setActionError(formatApiError(requestError) || "Notifications could not be marked as read.");
    } finally {
      setMarkingAll(false);
    }
  };

  const typeIcon = {
    info: <Info className="h-4 w-4 text-blue-500" />,
    success: <CheckCircle className="h-4 w-4 text-green-500" />,
    warning: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
    appointment: <Calendar className="h-4 w-4 text-blue-400" />,
    demand: <Megaphone className="h-4 w-4 text-amber-400" />,
  };

  // Issue #23: do not replace the whole page with a "Loading…" line. The header
  // and tabs stay mounted and only the notification list below renders skeleton
  // cards while loading (handled in the main return).

  const unreadCount = notifications.filter((n) => !isNotificationRead(n)).length;

  const filteredNotifs = notifications.filter((n) => {
    if (activeTab === "all") return true;
    if (activeTab === "visits") return n.type === "appointment";
    if (activeTab === "demands") return n.type === "demand";
    if (activeTab === "system") return ["info", "success", "warning"].includes(n.type);
    return true;
  });

  return (
    <div className="p-6 space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Animated gradient header icon */}
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary flex items-center justify-center shadow-sm animate-float-soft">
            <Bell className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-display uppercase tracking-tight">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              {unreadCount} unread alert{unreadCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} disabled={markingAll} className="font-bold gap-1">
            {markingAll ? <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />} {markingAll ? "Updating…" : "Mark All Read"}
          </Button>
        )}
      </div>

      {loadError && (
        <Card className="border-destructive/30 bg-destructive/5 p-6 text-center" role="alert">
          <p className="font-bold text-foreground">Notifications could not be loaded</p>
          <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
          <Button type="button" variant="outline" className="mt-4 gap-2" onClick={loadData}><RefreshCw className="h-4 w-4" aria-hidden="true" />Try again</Button>
        </Card>
      )}
      {partialError && !loadError && <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground" role="status">Some related updates are unavailable. {partialError}</p>}
      {actionError && <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">{actionError}</p>}

      {/* Tabs Selector */}
      <div className="w-full sm:w-fit bg-card border border-border/80 p-1 rounded-xl overflow-x-auto scrollbar-none relative flex gap-1">
        {NOTIF_TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none text-center px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                isActive
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`notif-filter-tab-${tab.id}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeNotifTab"
                  className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
              }`}>
                {notifications.filter(n => {
                  if (tab.id === "all") return true;
                  if (tab.id === "visits") return n.type === "appointment";
                  if (tab.id === "demands") return n.type === "demand";
                  if (tab.id === "system") return ["info", "success", "warning"].includes(n.type);
                  return true;
                }).length}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-4 border border-border/80 rounded-2xl animate-pulse space-y-2 bg-card">
              <div className="flex justify-between items-start">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 bg-muted rounded w-48" />
                    <div className="h-3 bg-muted rounded w-80" />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : loadError ? null : filteredNotifs.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center">
          <span className="inline-flex h-16 w-16 mb-3 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary animate-float-soft"><Bell className="h-8 w-8" /></span>
          <p className="font-medium">No {activeTab !== "all" ? activeTab.replace("visits", "site visit").replace("demands", "Plots on Demand") : ""} notifications found</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredNotifs.map((n) => {
            const isRead = isNotificationRead(n);
            const nid = n.id || n._id;
            return (
              <Card
                key={nid}
                className={`flex items-start gap-4 p-4 border rounded-2xl transition-all duration-200 hover:border-primary/50 hover:shadow-sm ${
                  isRead ? "bg-card/40 border-border/60" : "bg-primary/5 border-primary/20 shadow-sm"
                }`}
              >
                <div className="mt-0.5 shrink-0 w-8 h-8 rounded-xl bg-secondary/80 flex items-center justify-center border border-border">
                  {typeIcon[n.type] || typeIcon.info}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {n.type === "appointment" && (
                      <span className="text-[9px] font-black uppercase tracking-wider text-blue-650 dark:text-blue-400 bg-blue-500/10 border border-blue-500/25 px-2 py-0.5 rounded">
                        Appointment
                      </span>
                    )}
                    {n.type === "demand" && (
                      <span className="text-[9px] font-black uppercase tracking-wider text-amber-650 dark:text-amber-400 bg-amber-500/10 border border-amber-500/25 px-2 py-0.5 rounded">
                        Plots on Demand
                      </span>
                    )}
                    {!isRead && (
                      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    )}
                  </div>
                  <p className={`text-sm leading-relaxed ${isRead ? "text-muted-foreground" : "text-foreground font-semibold"}`}>
                    {n.message || n.title}
                  </p>
                  {n.created_at && (
                    <p className="text-xs text-muted-foreground/80 mt-1.5">
                      {new Date(n.created_at).toLocaleString("en-IN")}
                    </p>
                  )}
                  {n.type === "appointment" && (
                    <div className="mt-3">
                      <Button asChild size="sm" variant="secondary" className="gap-1 font-bold text-xs bg-slate-900 text-white hover:bg-slate-800 border border-border">
                        <Link to={visitDashboardPath} onClick={(e) => e.stopPropagation()}>
                          Open Visits Queue <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </Button>
                    </div>
                  )}
                  {n.type === "demand" && (
                    <div className="mt-3">
                      <Button asChild size="sm" variant="secondary" className="gap-1 font-bold text-xs bg-slate-900 text-white hover:bg-slate-800 border border-border">
                        <Link to={demandDashboardPath(n)} onClick={(e) => e.stopPropagation()}>
                          Open Details <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
                {!isRead && (
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); markRead(nid); }} className="shrink-0 text-xs text-muted-foreground hover:text-primary">
                    Mark read
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
